/**
 * FindReplaceDialog — fix one wrong value across EVERY instance row at once:
 * pick what is in the table ("From") and what it should be ("To"), for any of
 * Instance Type, Region and Pricing Model, then Replace all.
 *
 * Port of legacy ManagePortfolio/ActionButtons.vue (dialog :105-215), its
 * wording and order: title "Find and Replace", one From -> To pair per field,
 * Cancel and "Replace all". The rules (lists, when Replace all is allowed,
 * the request) live in ../findReplaceRules.js; the page does the request.
 *
 * Fixed from legacy: a failed replace keeps the dialog OPEN with its choices
 * (legacy closed it and wiped the table), and it cannot be closed while the
 * server is working. Legacy's arrow "Hide / Show" toggles only turned the icon
 * and hid nothing — a plain arrow here.
 *
 * Mounted only while open, so each opening starts blank, as legacy resets all
 * six values on open.
 */
import { useMemo, useState } from 'react';

import { Dropdown } from '@components/common/Dropdown';
import { ArrowRightIcon } from '@components/common/icons';
import { Button, Modal } from '@components/ui';

import {
  FIND_REPLACE_FIELDS,
  PRICING_TO_OPTIONS,
  emptyPairs,
  fromOptions,
  isReplaceReady,
} from '../findReplaceRules';
import { FIELD } from '../portfolioFormRules';

import styles from './FindReplaceDialog.module.css';

/**
 * @param {{ rows: object[],
 *           regions: { list: string[], loading?: boolean, error?: string },
 *           instanceTypes: { list: string[], loading?: boolean, error?: string },
 *           busy?: boolean,
 *           onReplace: (pairs: object) => void,
 *           onClose: () => void }} props
 */
export function FindReplaceDialog({
  rows,
  regions,
  instanceTypes,
  busy = false,
  onReplace,
  onClose,
}) {
  const [pairs, setPairs] = useState(emptyPairs);

  // The From lists come from the table as it is now.
  const from = useMemo(
    () =>
      Object.fromEntries(
        FIND_REPLACE_FIELDS.map(({ key, field }) => [key, fromOptions(rows, field)]),
      ),
    [rows],
  );

  const toFor = (field) =>
    field === FIELD.REGION
      ? regions
      : field === FIELD.INSTANCE_TYPE
        ? instanceTypes
        : { list: PRICING_TO_OPTIONS };

  const set = (key, side, value) =>
    setPairs((p) => ({ ...p, [key]: { ...p[key], [side]: value ?? '' } }));

  const ready = isReplaceReady(pairs);

  return (
    <Modal
      open
      onClose={busy ? () => {} : onClose}
      title="Find and Replace"
      footer={
        <div className={styles.actions}>
          <Button variant="secondary-solid" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!ready || busy}
            onClick={() => onReplace(pairs)}
          >
            Replace all
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        {FIND_REPLACE_FIELDS.map(({ key, field, label }) => {
          const to = toFor(field);
          return (
            <fieldset key={key} className={styles.section} disabled={busy}>
              <legend className={styles.sectionTitle}>{label}</legend>
              <div className={styles.pair}>
                <Dropdown
                  id={`find-replace-${key}-from`}
                  className={styles.field}
                  label="From"
                  options={from[key]}
                  value={pairs[key].from}
                  onChange={(v) => set(key, 'from', v)}
                  searchable
                  clearable
                  hideDetails
                  disabled={busy}
                />
                <ArrowRightIcon className={styles.arrow} aria-hidden="true" />
                <Dropdown
                  id={`find-replace-${key}-to`}
                  className={styles.field}
                  label="To"
                  options={to.list}
                  value={pairs[key].to}
                  onChange={(v) => set(key, 'to', v)}
                  loading={to.loading}
                  error={to.error}
                  searchable
                  clearable
                  hideDetails={!to.error}
                  disabled={busy}
                />
              </div>
            </fieldset>
          );
        })}
      </div>
    </Modal>
  );
}

export default FindReplaceDialog;
